import { apiUtils } from "../hooks"
import { throwApiError } from "../utils"
import { API_ENDPOINTS } from "./apiEndpoints"

export interface ParentEnterprise {
    id: string;
    name: string;
    code: string;
    created_date: string;
    last_modified_date: string;
    metadata: ParentEnterpriseMetadata;
}

export interface ParentEnterpriseMetadata {
    enterprise_count: number;
    assigned_count: number;
    unassigned_count: number;
    user_count: number;
}

interface UseParentEnterprisesParams {
    search?: string;
    page?: number;
    limit?: number;
    primarySortKey?: string;
    primarySortOrder?: 'asc' | 'desc' | null;
}

class ParentEnterprisesService {
    async getParentEnterprises(queryParams: UseParentEnterprisesParams): Promise<any> {
        try {
            const response = await apiUtils.get<any>(
                API_ENDPOINTS.parentEnterprises.list,
                queryParams
            )
            return response.data
        } catch (error) {
            throwApiError(error, 'Failed to fetch parent enterprises')
            return []
        }
    }

    async getParentEnterpriseById(id: string): Promise<ParentEnterprise> {
        try {
            const response = await apiUtils.get<ParentEnterprise>(
                API_ENDPOINTS.parentEnterprises.detail(id)
            )
            return response.data.data
        } catch (error) {
            throwApiError(error, `Failed to fetch parent enterprise with id ${id}`)
            throw error
        }
    }

    async createParentEnterprise(data: Partial<ParentEnterprise>): Promise<ParentEnterprise> {
        try {
            const response = await apiUtils.post<ParentEnterprise>(
                API_ENDPOINTS.parentEnterprises.create,
                data
            )
            return response.data.data
        } catch (error) {
            throwApiError(error, 'Failed to create parent enterprise')
            return {} as ParentEnterprise
        }
    }

    async updateParentEnterprise(id: string, data: Partial<ParentEnterprise>): Promise<ParentEnterprise> {
        try {
            const response = await apiUtils.put<ParentEnterprise>(
                API_ENDPOINTS.parentEnterprises.update(id),
                data
            )
            return response.data.data
        } catch (error) {
            throwApiError(error, `Failed to update parent enterprise with id ${id}`)
            return {} as ParentEnterprise
        }
    }

    async deleteParentEnterprise(id: string): Promise<void> {
        try {
            await apiUtils.delete<void>(API_ENDPOINTS.parentEnterprises.delete(id))
        } catch (error) {
            throwApiError(error, `Failed to delete parent enterprise with id ${id}`)
        }
    }

    async getAssignedEnterprises(
        parentEnterpriseId: string,
        page: number = 1,
        limit: number = 10,
        searchTerm: string = '',
        sortKey: string | null = null,
        sortOrder: 'asc' | 'desc' | null = null
    ): Promise<any> {
        try {
            const response = await apiUtils.get(
                API_ENDPOINTS.parentEnterprises.assignedEnterprises(parentEnterpriseId),
                {
                    page,
                    limit,
                    search: searchTerm,
                    primarySortKey: sortKey,
                    primarySortOrder: sortOrder,
                }
            )
            return response?.data
        } catch (error) {
            throwApiError(error, `Failed to fetch assigned enterprises for parent enterprise with id ${parentEnterpriseId}`)
            return []
        }
    }

    async getUnassignedEnterprises(
        parentEnterpriseId: string,
        page: number = 1,
        limit: number = 10,
        searchTerm: string = '',
        sortKey: string | null = null,
        sortOrder: 'asc' | 'desc' | null = null
    ): Promise<any> {
        try {
            const response = await apiUtils.get(
                API_ENDPOINTS.parentEnterprises.unassignedEnterprises(parentEnterpriseId),
                {
                    page,
                    limit,
                    search: searchTerm,
                    primarySortKey: sortKey,
                    primarySortOrder: sortOrder,
                }
            )
            return response?.data
        } catch (error) {
            throwApiError(error, `Failed to fetch unassigned enterprises for parent enterprise with id ${parentEnterpriseId}`)
            return []
        }
    }

    async assignEnterprises(parentEnterpriseId: string, enterpriseIds: string[]): Promise<any> {
        try {
            const response = await apiUtils.post(
                API_ENDPOINTS.parentEnterprises.assignEnterprises(parentEnterpriseId),
                { enterpriseIds: enterpriseIds }
            )
            return response?.data
        } catch (error) {
            throwApiError(error, 'Failed to assign enterprises')
        }
    }

    async unassignEnterprises(parentEnterpriseId: string, enterpriseIds: string[]): Promise<any> {
        try {
            const response = await apiUtils.post(
                API_ENDPOINTS.parentEnterprises.unassignEnterprises(parentEnterpriseId),
                { enterpriseIds: enterpriseIds }
            )
            return response?.data
        } catch (error) {
            throwApiError(error, 'Failed to unassign enterprises')
        }
    }

    async getLinkedUsers(parentEnterpriseId: string, searchValue?: string): Promise<any> {
        try {
            const response = await apiUtils.get(
                API_ENDPOINTS.parentEnterprises.linkedUsers(parentEnterpriseId)
            )
            return response.data
        } catch (error) {
            throwApiError(error, `Failed to fetch users for parent enterprise with id ${parentEnterpriseId}`)
            return []
        }
    }

    async linkUser(parentEnterpriseId: string, userId: string): Promise<any> {
        try {
            const response = await apiUtils.post(
                API_ENDPOINTS.parentEnterprises.linkUser(parentEnterpriseId),
                { user_id: userId }
            )
            return response?.data
        } catch (error) {
            throwApiError(error, 'Failed to link user to parent enterprise')
        }
    }

    async deleteUsers(parentEnterpriseId: string, userIds: string[]): Promise<any> {
        try {
            const response = await apiUtils.delete(
                API_ENDPOINTS.parentEnterprises.deleteUsers(parentEnterpriseId),
                { data: { user_ids: userIds } }
            )
            return response
        } catch (error) {
            throwApiError(error, 'Failed to delete users from parent enterprise')
        }
    }

    async searchUsers(parentEnterpriseId: string, searchValue: string): Promise<any> {
        try {
            const response = await apiUtils.get(
                API_ENDPOINTS.parentEnterprises.searchUsers(parentEnterpriseId),
                { search: searchValue }
            )
            return response.data
        } catch (error) {
            throwApiError(error, `Failed to search users for parent enterprise with id ${parentEnterpriseId}`)
            return []
        }
    }
}

export const parentEnterprisesService = new ParentEnterprisesService()